define(() => {
    "use strict";

    return {
        detect(userAgent) {
            userAgent = userAgent || window.navigator.userAgent;

            const ANDROID_PATTERN = /Android/;
            const CHROME_PATTERN = /(?:Chrome|CriOS)\/([0-9.]+)/;

            /*
             * Apple devices remove Safari from user-agent when using the
             * apple-mobile-web-app-capable meta tag. If this happens we
             * fallback to detecting on iPad/iPhone presence.
             */
            const SAFARI_PATTERN = /Safari|(?:(?:iPad|iPhone).*AppleWebKit.*Mobile)/;
            const SAFARI_VERSION_PATTERN = /Version\/([0-9.]+)/;
            const FIREFOX_PATTERN = /Firefox\/([0-9.]+)/;
            const EDGE_PATTERN    = /Edge/i;
            const EDGE_VERSION_PATTERN = /Edge\/([0-9.]+)/;
            const INTERNET_EXPLORER_PATTERN = /Trident|MSIE/;
            const INTERNET_EXPLORER_VERSION_PATTERN = /(?:MSIE |rv:)([0-9.]+)/;
            const GOOGLEBOT_PATTERN = /Googlebot\/([0-9.]+)/;
            const OPERA_PATTERN = /Opera.*Version\/([0-9.]+)|OPR\/([0-9.]+)/;

            // Currently vaguely expressed as Facebook, Twitter or Instagram specific UA's. See MOS-3042
            const WEB_VIEW_PATTERN = /FBAN|FB_IAB|FB4A|FBAV|Twitter|Instagram/;
            const MOBILE_PATTERN = /Mobile/;   // used for smartphones.
            const TABLET_PATTERN = /Tablet/;   // used for Tablets.
            const IPOD_PATTERN = /iPod/;
            const IPHONE_PATTERN = /iPhone/;
            const IPAD_PATTERN = /iPad/;
            const WEB_KIT_PATTERN = /AppleWebKit/;
            const DEFAULT_BROWSER_VERSION = [];
            const DEFAULT_OS_VERSION = [];

            /*
             * List of supported OS version patterns.
             */
            const ANDROID_VERSION_PATTERN = /Android ([._\d]+)/;
            const IOS_VERSION_PATTERN = /OS ([._\d]+)/;
            const MAC_VERSION_PATTERN = /Mac OS X ([._\d]+)/;
            const WINDOWS_VERSION_PATTERN = /Windows\s+NT\s+([\d.]+)/;


            /**
             * Tests for Android devices.
             *
             * @returns {Boolean} <code>true</code> if the current device is running
             *          on Android, <code>false</code> if not.
             */
            function isAndroid() {
                return ANDROID_PATTERN.exec(userAgent) !== null;
            }

            /**
             * Tests for MAC OS.
             *
             * @returns {Boolean} <code>true</code> if the current device is running
             *          on Mac, <code>false</code> if not.
             */
            function isMacOS() {
                return userAgent.indexOf("Mac OS X") !== -1;
            }

            /**
             * Tests for Windows OS.
             *
             * @returns {Boolean} <code>true</code> if the current device is running
             *          on Windows, <code>false</code> if not.
             */
            function isWindows() {
                return userAgent.indexOf("Windows") !== -1;
            }

            /**
             * Tests for iOS.
             *
             * @returns {Boolean} <code>true</code> if the current device is running
             *          on iOS, <code>false</code> if not.
             */
            function isIOS() {
                return isIPod() || isIPhone() || isIPad();
            }

            /**
             * Tests for iPod.
             *
             * @returns {Boolean} <code>true</code> if the device is an iPod Touch, <code>false</code> if not.
             */
            function isIPod() {
                return IPOD_PATTERN.exec(userAgent) !== null;
            }

            /**
             * Tests for iPad.
             *
             * @returns {Boolean} <code>true</code> if the device is an iPad, <code>false</code> if not.
             */
            function isIPad() {
                return IPAD_PATTERN.exec(userAgent) !== null;
            }

            /**
             * Tests for iPhone.
             *
             * @returns {Boolean} <code>true</code> if the device is an iPhone, <code>false</code> if not.
             */
            function isIPhone() {
                return IPHONE_PATTERN.exec(userAgent) !== null;
            }

            /**
             * Whether we believe we're inside a WebView, see <tt>WEB_VIEW_PATTERN</tt>.
             *
             * Currently we match Facebook, Twitter and Instagram.
             *
             * @returns {Boolean} <code>true</code> if we're inside a WebView.
             */
            function isWebView() {
                return WEB_VIEW_PATTERN.exec(userAgent) !== null;
            }

            /**
             * Tests for Microsoft Edge.
             *
             * @returns {Boolean} <code>true</code> if the current browser is Microsoft Edge, <code>false</code> if not.
             */
            function isEdge() {
                return EDGE_PATTERN.exec(userAgent) !== null;
            }

            /**
             * Tests for Chrome.
             *
             * @returns {Boolean} <code>true</code> if the current browser is using Chrome engine, <code>false</code> if not.
             */
            function isChrome() {
                return CHROME_PATTERN.exec(userAgent) !== null && !isEdge(); // Edge pretends to be Chrome
            }

            /**
             * Tests for Webkit.
             *
             * @returns {Boolean} <code>true</code> if the current browser is using Webkit engine, <code>false</code> if not.
             */
            function isWebkit() {
                return WEB_KIT_PATTERN.exec(userAgent) !== null && !isEdge(); // Edge pretends to be webkit
            }

            /**
             * Tests for mobile devices.
             *
             * @returns {Boolean} <code>true</code> if the current browser is a mobile one, <code>false</code> if not.
             */
            function isMobile() {
                return MOBILE_PATTERN.exec(userAgent) !== null;
            }

            /**
             * Tests for tablet devices.
             *
             * @returns {Boolean} <code>true</code> if the current device is a tablet, <code>false</code> if not.
             */
            function isTablet() {
                // Tablet on Android Chrome does not contain the Mobile in the user string.
                if (isAndroid() && isChrome()) {
                    return !isMobile();
                }

                // Some iOS devices do not contain the Tablet string.
                return TABLET_PATTERN.exec(userAgent) !== null || isIPad();
            }

            /**
             * Get the version of the currently detected browser. This method
             * returns an array of integers containing the browser version,
             * starting with the major version (e.g. version "12.5.2.67"
             * becomes [12,5,2,67]).
             *
             * Note that any letters contained in versions will be subject to
             * the parseInt method (e.g. "15.0a1"). Firefox has a history of
             * doing this for dev releases.
             *
             * @returns {number[]} The version as an array, or empty array if unable to determine
             */
            function getBrowserVersion(pattern, matchIndex) {
                if (!pattern) {
                    return DEFAULT_BROWSER_VERSION;
                } else if (!matchIndex) {
                    matchIndex = 1;
                }

                const match = pattern.exec(userAgent);
                if (match) {
                    const versions = match[matchIndex].split(".");
                    let idx = versions.length;

                    // Convert versions from string to int.
                    while (idx--) {
                        versions[idx] = parseInt(versions[idx], 10);
                    }
                    return versions;
                }
                return DEFAULT_BROWSER_VERSION;
            }

            /**
             * Get the Operative system version where the the currently detected browser is operating.
             * This method returns an array of integers containing the browser version,
             * starting with the major version (e.g. version "12.5.2.67" becomes [12,5,2,67]).
             * @returns {number[]} The version as an array, or empty array if unable to determine
             */
            function getOSVersion() {
                let operatingSystemInfo = null;
                if (isAndroid()) {
                    operatingSystemInfo = ANDROID_VERSION_PATTERN.exec(userAgent);
                } else if (isIOS()) {
                    operatingSystemInfo = IOS_VERSION_PATTERN.exec(userAgent);
                } else if (isWindows()) {
                    operatingSystemInfo = WINDOWS_VERSION_PATTERN.exec(userAgent);
                } else if (isMacOS()) {
                    operatingSystemInfo = MAC_VERSION_PATTERN.exec(userAgent);
                }

                // operatingSystemInfo is an array containing OS Name and OS version number, e.g. ["Android", "4.4"].
                if (operatingSystemInfo && operatingSystemInfo.length === 2) {
                    const versionArray = operatingSystemInfo[1]
                        .replace(/_/g, ".") // Normalize the array as some Android user agent contains _ instead of .
                        .split(".");
                    let i = 0;

                    for (i = 0; i < versionArray.length; i++) {
                        versionArray[i] = parseInt(versionArray[i], 10);
                    }

                    return versionArray;
                }

                return DEFAULT_OS_VERSION;
            }

            const result = {
                desktop:        false,
                tablet:         isTablet(),
                mobile:         isMobile(),

                android:        isAndroid(),
                iOS:            isIOS(),
                macOS:          isMacOS(),
                winOS:          isWindows(),
                osVersion:      getOSVersion(),

                // Being inside a WebView doesn't leave out browser detection, e.g. we could be inside a Chrome based WebView.
                webView:        isWebView(),

                webkit:         isWebkit(),
                ie:             false,
                firefox:        false,
                safari:         false,
                chrome:         false,
                googlebot:      false,
                opera:          false,
                edge:           false,
                version:        DEFAULT_BROWSER_VERSION
            };

            // Check mobile/desktop...
            result.desktop = !(result.iOS || result.android || result.mobile || result.tablet);

            /*
             * NOTE: The order of checks is important as opera 15+ will
             * disguise itself as chrome and safari. See the test spec
             * for example of opera user agent.
             */
            if (OPERA_PATTERN.exec(userAgent) !== null) {
                const match = OPERA_PATTERN.exec(userAgent);
                if (match[1]) {
                    result.version = getBrowserVersion(OPERA_PATTERN, 1);
                } else {
                    result.version = getBrowserVersion(OPERA_PATTERN, 2);
                }
                result.opera = true;
            } else if (isEdge()) {
                result.edge = true;
                result.version = getBrowserVersion(EDGE_VERSION_PATTERN);
            } else if (INTERNET_EXPLORER_PATTERN.exec(userAgent) !== null) {
                result.ie = true;
                result.version = getBrowserVersion(INTERNET_EXPLORER_VERSION_PATTERN);
            } else if (FIREFOX_PATTERN.exec(userAgent) !== null) {
                result.firefox = true;
                result.version = getBrowserVersion(FIREFOX_PATTERN);
            } else if (isChrome()) {
                result.chrome = true;
                result.version = getBrowserVersion(CHROME_PATTERN);
            } else if (GOOGLEBOT_PATTERN.exec(userAgent) !== null) {
                result.googlebot = true;
                result.version = getBrowserVersion(GOOGLEBOT_PATTERN);
            // Some android devices (e.g. Motorola Xoom with android 4.0) match the safari pattern, so we
            // test explicitly that it isn't android.
            } else if (!result.android && SAFARI_PATTERN.exec(userAgent) !== null) {
                result.safari = true;
                result.version = getBrowserVersion(SAFARI_VERSION_PATTERN);
            }

            return result;
        }
    };
});



// WEBPACK FOOTER //
// ./src/shared/util/BrowserDetector.js